/// <reference path="./better-sqlite3-session-store.d.ts" />
import Logger from '@/logger';
import soft from '@/softconfig';
import { openDb } from '@/db/connection';
import { runMigrations } from '@/db/migrate';
import { importNedbIfNeeded } from '@/db/nedbImport';
import { type IServerConfig } from '@config/types';
import type Omegga from '@omegga/server';
import { type IServerStatus } from '@omegga/types';
import { createExpressMiddleware } from '@trpc/server/adapters/express';
import bodyParser from 'body-parser';
import { drizzle } from 'drizzle-orm/better-sqlite3';
import express from 'express';
import expressSession from 'express-session';
import hasbin from 'hasbin';
import http from 'http';
import https from 'https';
import BetterSqlite3SessionStore from 'better-sqlite3-session-store';
import path from 'path';
import bcrypt from 'bcryptjs';
import {
  generateAuthenticationOptions,
  verifyAuthenticationResponse,
} from '@simplewebauthn/server';
import Database from './database';
import { verifyToken as verifyTOTP } from './totp';
import setupMetrics from './metrics';
import { appRouter } from './router';
import { setWebserver } from './router/server';
import { createContext, setContextDeps } from './trpc';
import * as util from './util';

// path to vite-built data
const PUBLIC_PATH = path.join(__dirname, '../../../public');
const ASSETS_PATH = path.join(__dirname, '../../../public/assets');

const log = (...args: unknown[]) => Logger.log(...args);
const error = (...args: unknown[]) => Logger.error(...args);

// the webserver serves an authenticated web UI
export default class Webserver {
  database: Database;
  port: number;
  omegga: Omegga;

  app: express.Express;
  // undefined until createServer() picks a protocol (http/https)
  server: http.Server | https.Server | undefined;

  options: IServerConfig;
  https: boolean;
  /** host shown in the "Web UI available at" log (OMEGGA_UI_HOST) */
  host: string;
  started: boolean;
  created: Promise<boolean>;

  // assigned by setupMetrics once the server status loop starts
  serverStatusInterval: ReturnType<typeof setInterval> | undefined;
  lastReportedStatus: IServerStatus | null = null;

  dataPath: string;

  // create a webserver
  constructor(options: IServerConfig, omegga: Omegga) {
    this.port = Number(
      options.port || process.env.OMEGGA_PORT || soft.DEFAULT_PORT,
    );
    this.host = process.env.OMEGGA_UI_HOST || '127.0.0.1';
    this.options = options;
    this.omegga = omegga;
    this.dataPath = path.join(omegga.path, soft.DATA_PATH);

    // create express app (routes are attached in createServer/initWebUI)
    this.app = express();

    const mainSqlite = openDb(path.join(this.dataPath, soft.MAIN_DB));
    const mainDb = drizzle(mainSqlite);

    runMigrations(mainDb);

    // the database provides omegga with metrics, chat logs, and more
    // to help administrators keep track of their users and server
    this.database = new Database(options, omegga, mainSqlite, mainDb);

    // https status of the server
    this.https = false;
    // started status of the server
    this.started = false;
    this.created = this.createServer();
  }

  // create the webserver
  async createServer() {
    const hasOpenSSL = hasbin.sync('openssl');

    await importNedbIfNeeded(
      this.dataPath,
      this.database.sqlite,
      this.database.db,
    );

    // create a server with https if applicable
    const pickProtocol = async () => {
      if (this.options.https) {
        if (hasOpenSSL) {
          const certFile = await util.getSSLKeys(this.dataPath);
          if (certFile) {
            // handy notification of generated ssl certs
            if (certFile.new) log('>>'.green, 'Generated new SSL certificate');

            this.https = true;
            return https.createServer(
              {
                key: certFile.keys.serviceKey,
                cert: certFile.keys.certificate,
              },
              this.app,
            );
          } else {
            error(
              '!>'.red,
              'Error generating SSL certificate - falling back to http',
            );
          }
        }

        // warn the user
        log(
          ':>'.yellow,
          'Running web server with http - install',
          'openssl'.yellow.underline,
          'for https (more secure)',
        );
      }

      // fallback to http
      return new http.Server(this.app);
    };

    // create http(s) server based on openssl availability
    this.server = await pickProtocol();

    this.app.set('trust proxy', 1);
    const SqliteStore = (
      BetterSqlite3SessionStore['default'] ?? BetterSqlite3SessionStore
    )(expressSession);
    const sessionSqlite = openDb(path.join(this.dataPath, soft.SESSIONS_DB));
    const session = expressSession({
      secret: util.getSessionSecret(this.dataPath),
      resave: false,
      saveUninitialized: false,
      cookie: {
        path: '/',
        secure: this.https,
        maxAge: 365 * 24 * 60 * 60 * 1000, // 1 year
      },
      store: new SqliteStore({
        client: sessionSqlite,
        expired: { clear: true, intervalMs: 900000 },
      }),
    });
    this.app.use(session);

    // setup routes and webserver
    await this.initWebUI();
    return true;
  }

  // setup the web ui routes
  async initWebUI() {
    await this.database.getInstanceId();

    // Initialize tRPC context deps
    setContextDeps({
      database: this.database,
      omegga: this.omegga,
    });
    setWebserver(this);

    // provide assets in the /public path
    this.app.use('/assets', express.static(ASSETS_PATH));
    this.app.use(bodyParser.json());

    // --- Auth routes (outside tRPC) ---
    const openApi = express.Router();
    const api = express.Router();

    // express-session leaves req.session undefined on requests it declines,
    // like a request target that is not a path (`OPTIONS *`)
    const requireSession: express.RequestHandler = (req, res, next) =>
      req.session ? next() : res.status(400).json({ message: 'no session' });

    // check if this is the first user in the database
    openApi.get('/first', async (_req, res) =>
      res.json(await this.database.isFirstUser()),
    );

    // login / create admin user route
    openApi.post('/auth', async (req, res) => {
      if (
        typeof req.body !== 'object' ||
        typeof req.body.username !== 'string' ||
        typeof req.body.password !== 'string'
      ) {
        return res.status(422).json({ message: 'invalid body' });
      }
      const { username, password } = req.body;

      if (!username.match(/^\w{0,32}$/)) {
        return res.status(422).json({ message: 'invalid body' });
      }

      const isFirst = await this.database.isFirstUser();
      let user;
      if (isFirst) {
        user = await this.database.createAdminUser(
          username,
          username === '' ? '' : password,
        );
      } else {
        user = await this.database.authUser(username, password);
      }

      if (user) {
        req.session.userId = user._id;
        if (user.totpEnabled) {
          req.session.mfaPending = true;
          req.session.save();
          res.status(200).json({ mfaRequired: true });
        } else {
          req.session.save();
          res.status(200).json({});
        }
      } else {
        res.status(404).json({ message: 'no user found' });
      }
    });

    // rate limiting for MFA verification (per session)
    const mfaAttempts = new Map<
      string,
      { count: number; lockedUntil: number }
    >();

    // TOTP verification during MFA challenge
    openApi.post('/auth/mfa/totp', async (req, res) => {
      if (!req.session.mfaPending || !req.session.userId) {
        return res.status(400).json({ message: 'no pending MFA challenge' });
      }

      // rate limit: 5 attempts, then 60s lockout
      const sid = req.sessionID;
      const attempt = mfaAttempts.get(sid) ?? { count: 0, lockedUntil: 0 };
      if (Date.now() < attempt.lockedUntil) {
        return res
          .status(429)
          .json({ message: 'too many attempts, try again later' });
      }

      const { code } = req.body ?? {};
      if (typeof code !== 'string') {
        return res.status(422).json({ message: 'code required' });
      }
      const user = await this.database.findUserById(req.session.userId);
      if (!user || !user.totpSecret) {
        return res.status(400).json({ message: 'MFA not configured' });
      }

      let verified = false;

      if (verifyTOTP(code, user.totpSecret)) {
        verified = true;
      }

      // try recovery codes
      if (!verified && user.recoveryCodes?.length) {
        for (const hashedCode of user.recoveryCodes) {
          if (await bcrypt.compare(code, hashedCode)) {
            await this.database.removeRecoveryCode(user.username, hashedCode);
            verified = true;
            break;
          }
        }
      }

      if (verified) {
        mfaAttempts.delete(sid);
        delete req.session.mfaPending;
        req.session.save();
        return res.status(200).json({});
      }

      attempt.count++;
      if (attempt.count >= 5) {
        attempt.lockedUntil = Date.now() + 60_000;
        attempt.count = 0;
      }
      mfaAttempts.set(sid, attempt);
      res.status(401).json({ message: 'invalid code' });
    });

    const getRpID = (req: express.Request) => {
      const origin = req.headers.origin;
      if (origin) {
        try {
          return new URL(origin as string).hostname;
        } catch {}
      }
      return req.hostname;
    };

    // WebAuthn authentication options (passwordless login)
    openApi.get('/auth/webauthn/options', async (req, res) => {
      const rpID = getRpID(req);
      const options = await generateAuthenticationOptions({ rpID });
      req.session.mfaChallenge = options.challenge;
      req.session.save();
      res.json(options);
    });

    // WebAuthn authentication verification
    openApi.post('/auth/webauthn/verify', async (req, res) => {
      const challenge = req.session.mfaChallenge;
      if (!challenge) {
        return res.status(400).json({ message: 'no pending challenge' });
      }
      // clear challenge immediately to prevent replay
      delete req.session.mfaChallenge;
      req.session.save();
      try {
        const { credential } = req.body;
        const user = await this.database.findUserByPasskeyId(credential?.id);
        if (!user || user.isBanned) {
          return res.status(401).json({ message: 'unknown credential' });
        }
        const passkey = user.passkeys!.find(p => p.id === credential.id)!;
        const verification = await verifyAuthenticationResponse({
          response: credential,
          expectedChallenge: challenge,
          expectedOrigin:
            req.headers.origin ?? `${req.protocol}://${req.get('host')}`,
          expectedRPID: getRpID(req),
          credential: {
            id: passkey.id,
            publicKey: Buffer.from(passkey.publicKey, 'base64url'),
            counter: passkey.counter,
            transports: passkey.transports,
          },
        });
        if (!verification.verified) {
          return res.status(401).json({ message: 'verification failed' });
        }
        await this.database.updatePasskeyCounter(
          user.username,
          passkey.id,
          verification.authenticationInfo.newCounter,
        );
        req.session.userId = user._id;
        delete req.session.mfaPending;
        req.session.save();
        res.status(200).json({});
      } catch {
        res.status(400).json({ message: 'verification error' });
      }
    });

    // authentication middleware for protected api routes
    api.all('*', (req, _res, next) => {
      (async () => {
        if (req.session.mfaPending) return next(new Error('unauthorized'));
        const user = await this.database.findUserById(req.session.userId);
        if (!user || user.isBanned) return next(new Error('unauthorized'));
        next();
      })();
    });

    // kill a session
    api.get('/logout', (req, res) => {
      req.session.destroy(e => {
        res.status(e ? 500 : 200).json({});
      });
    });

    this.app.use('/api/v1', requireSession);
    this.app.use('/api/v1', openApi);
    this.app.use('/api/v1', api);

    // --- tRPC ---
    this.app.use(
      '/trpc',
      createExpressMiddleware({
        router: appRouter,
        createContext,
      }),
    );

    // setup metrics and tracking
    setupMetrics(this);

    // every request goes through the index file (frontend handles 404s)
    this.app.use(async (req, res) => {
      if (req.session?.mfaPending) {
        return res.sendFile(path.join(PUBLIC_PATH, 'auth.html'));
      }
      const user = await this.database.findUserById(req.session?.userId);
      const isAuth = user && !user.isBanned;
      if (isAuth) {
        res.sendFile(path.join(PUBLIC_PATH, 'app.html'));
      } else {
        res.sendFile(path.join(PUBLIC_PATH, 'auth.html'));
      }
    });
  }

  // start the webserver
  async start() {
    if (this.started) return;
    await this.created;
    const server = this.server;
    if (!server) throw new Error('webserver failed to initialize');
    return await new Promise<void>(resolve => {
      server.listen(this.port, () => {
        log(
          `${'>>'.green} Web UI available at`,
          `http${this.https ? 's' : ''}://${this.host}:${this.port}`.green,
        );
        this.started = true;
        this.database.addChatLog('server', {}, 'Server started');
        resolve();
      });
    });
  }

  // stop the webserver
  stop() {
    this.database.addChatLog('server', {}, 'Server stopped');
    this.server?.close();
    this.started = false;
    clearInterval(this.serverStatusInterval);
  }
}
